// Enhanced switchgear data with descriptions, relationships, and metadata

export const SWITCHGEAR_DATA = [
  {
    id: "1",
    number: 1,
    name: "Main Contacts",
    color: "#3B82F6",
    failureModes: [
      {
        name: "Contact Erosion",
        description: "Gradual wearing away of contact material due to arcing during normal operations.",
        importance: "Eroded contacts increase resistance, generate heat, and eventually fail to carry rated current.",
        approach: "Measure contact wear indicators. Replace contacts when wear limit is reached.",
        severity: "Moderate",
        relatedTests: ["Contact Resistance Test", "X-ray Inspection"],
        relatedMonitoring: ["Thermal Imaging"]
      },
      {
        name: "Contact Welding",
        description: "Contacts fusing together from excessive current or slow operation, preventing opening.",
        importance: "Welded contacts cannot interrupt faults, creating major safety hazard.",
        approach: "Investigate cause - may indicate protection miscoordination or slow mechanism. Replace contacts.",
        severity: "Critical",
        relatedTests: ["Contact Resistance Test", "Dynamic Resistance"],
        relatedMonitoring: ["Thermal Imaging"]
      },
      {
        name: "Contact Misalignment",
        description: "Improper contact alignment causing uneven wear, poor contact, or incomplete closure.",
        importance: "Misalignment causes hot spots, accelerated wear, and potential failure to interrupt.",
        approach: "Adjust contact alignment per manufacturer specifications. May require mechanism work.",
        severity: "Moderate",
        relatedTests: ["X-ray Inspection", "Dynamic Resistance"],
        relatedMonitoring: ["Thermal Imaging"]
      },
      {
        name: "Surface Contamination",
        description: "Foreign material on contact surfaces reducing conductivity and causing heating.",
        importance: "Contamination increases resistance and can cause arcing even when closed.",
        approach: "Clean contacts with appropriate materials. Identify contamination source.",
        severity: "Moderate",
        relatedTests: ["Contact Resistance Test"],
        relatedMonitoring: ["Temperature Sensors"]
      },
      {
        name: "Overheating at Contacts",
        description: "Excessive temperature at contacts due to high resistance, overloading, or poor connection.",
        importance: "Can damage surrounding insulation and lead to catastrophic failure.",
        approach: "Identify cause - resistance, loading, or ambient issues. Address root cause.",
        severity: "Critical",
        relatedTests: ["Contact Resistance Test"],
        relatedMonitoring: ["Thermal Imaging", "Temperature Sensors"]
      }
    ],
    tests: [
      {
        name: "Contact Resistance Test",
        description: "DC resistance measurement across main contacts to verify low-resistance connection.",
        importance: "High resistance indicates wear, contamination, or poor contact pressure. Simple but critical test.",
        approach: "Inject 100-200A DC. Measure voltage drop. Calculate resistance and compare to limits.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Contact Erosion", "Contact Welding", "Surface Contamination", "Overheating at Contacts"]
      },
      {
        name: "Dynamic Resistance",
        description: "Continuous resistance measurement during breaker operation to analyze contact motion.",
        importance: "Reveals contact bounce, pre-strike arcing, and alignment issues not visible in static tests.",
        approach: "Record resistance during open/close operations. Analyze waveform for anomalies.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Contact Welding", "Contact Misalignment"]
      },
      {
        name: "X-ray Inspection",
        description: "Radiographic inspection of contact assembly in sealed breakers.",
        importance: "Only way to visually inspect contacts in sealed vacuum or SF6 interrupters.",
        approach: "Take X-ray images at multiple angles. Compare to baseline for wear assessment.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Contact Erosion", "Contact Misalignment"]
      }
    ],
    monitoring: [
      {
        name: "Thermal Imaging",
        description: "Periodic or continuous infrared monitoring of contact area temperatures.",
        importance: "Early detection of hot spots allows intervention before failure.",
        approach: "Scan during operation. Compare to baseline and between phases.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Contact Erosion", "Contact Welding", "Contact Misalignment", "Overheating at Contacts"]
      },
      {
        name: "Temperature Sensors",
        description: "Installed sensors monitoring contact temperature in real-time.",
        importance: "Continuous monitoring provides immediate warning of developing problems.",
        approach: "Install RTDs or fiber optic sensors near contacts. Set alarm thresholds.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Surface Contamination", "Overheating at Contacts"]
      }
    ]
  },
  {
    id: "2",
    number: 2,
    name: "Insulation System",
    color: "#10B981",
    failureModes: [
      {
        name: "Surface Tracking",
        description: "Conductive paths forming on insulation surfaces due to contamination and moisture.",
        importance: "Tracking leads to flashover across insulation surfaces, causing catastrophic failure.",
        approach: "Clean and dry insulation. May need to resurface or replace severely tracked parts.",
        severity: "Critical",
        relatedTests: ["Insulation Resistance", "Power Factor Test"],
        relatedMonitoring: ["PD Sensors", "Humidity Monitors"]
      },
      {
        name: "Insulation Breakdown",
        description: "Complete failure of insulation allowing current flow through the material.",
        importance: "Results in immediate failure and potential safety hazard. Often not repairable.",
        approach: "Replace failed insulation components. Investigate cause to prevent recurrence.",
        severity: "Critical",
        relatedTests: ["Insulation Resistance", "Partial Discharge Test"],
        relatedMonitoring: ["PD Sensors"]
      },
      {
        name: "Moisture Absorption",
        description: "Water absorption into insulation material reducing its dielectric strength.",
        importance: "Wet insulation has dramatically lower breakdown voltage and accelerated aging.",
        approach: "Dry insulation through heating or vacuum. Improve sealing to prevent reoccurrence.",
        severity: "Critical",
        relatedTests: ["Insulation Resistance", "Power Factor Test"],
        relatedMonitoring: ["Humidity Monitors"]
      },
      {
        name: "Partial Discharge",
        description: "Localized electrical discharges in voids or at interfaces within insulation.",
        importance: "PD progressively erodes insulation until complete breakdown. Early indicator of problems.",
        approach: "Locate PD source. Address cause - may be voids, contamination, or design issues.",
        severity: "Critical",
        relatedTests: ["Partial Discharge Test"],
        relatedMonitoring: ["PD Sensors", "UHF Sensors"]
      },
      {
        name: "Aging/Embrittlement",
        description: "Gradual degradation of insulation material properties over time.",
        importance: "Aged insulation is prone to cracking and has reduced electrical strength.",
        approach: "Assess remaining life through testing. Plan replacement based on condition.",
        severity: "Moderate",
        relatedTests: ["Power Factor Test", "Insulation Resistance"],
        relatedMonitoring: ["PD Sensors"]
      },
      {
        name: "Contamination",
        description: "Foreign material deposited on insulation surfaces reducing performance.",
        importance: "Contamination enables tracking and reduces flashover voltage.",
        approach: "Clean insulation thoroughly. Improve environmental controls or sealing.",
        severity: "Moderate",
        relatedTests: ["Insulation Resistance"],
        relatedMonitoring: ["Humidity Monitors"]
      }
    ],
    tests: [
      {
        name: "Partial Discharge Test",
        description: "Detection and measurement of partial discharge activity in insulation system.",
        importance: "Early warning of developing insulation faults. Can locate problem areas.",
        approach: "Apply voltage and measure PD using electrical or acoustic sensors. Analyze patterns.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Insulation Breakdown", "Partial Discharge"]
      },
      {
        name: "Insulation Resistance",
        description: "DC resistance measurement of insulation to ground and between phases.",
        importance: "Basic screening test for gross insulation problems. Simple but essential.",
        approach: "Apply DC test voltage. Measure resistance after 1 minute. Calculate PI if needed.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Surface Tracking", "Insulation Breakdown", "Moisture Absorption", "Contamination"]
      },
      {
        name: "Power Factor Test",
        description: "Measures dielectric losses in insulation system.",
        importance: "Detects moisture, contamination, and aging that IR test may miss.",
        approach: "Apply AC test voltage. Measure power factor and capacitance. Compare to baseline.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Surface Tracking", "Moisture Absorption", "Aging/Embrittlement"]
      }
    ],
    monitoring: [
      {
        name: "PD Sensors",
        description: "Continuous monitoring sensors for partial discharge activity.",
        importance: "Provides early warning of developing insulation problems.",
        approach: "Install capacitive or inductive sensors. Set alarm thresholds based on baseline.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Surface Tracking", "Insulation Breakdown", "Partial Discharge", "Aging/Embrittlement"]
      },
      {
        name: "Humidity Monitors",
        description: "Sensors monitoring moisture levels inside switchgear enclosure.",
        importance: "Early detection of moisture ingress allows intervention before damage.",
        approach: "Install humidity sensors. Set alarms for high levels. Add heaters if needed.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Surface Tracking", "Moisture Absorption", "Contamination"]
      },
      {
        name: "UHF Sensors",
        description: "Ultra-high frequency sensors detecting electromagnetic signals from PD.",
        importance: "Very sensitive PD detection method. Can locate sources in complex geometries.",
        approach: "Install UHF antennas inside enclosure. Analyze frequency patterns for PD signatures.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Partial Discharge"]
      }
    ]
  },
  {
    id: "3",
    number: 3,
    name: "Operating Mechanism",
    color: "#F59E0B",
    failureModes: [
      {
        name: "Spring Fatigue",
        description: "Weakening of stored-energy springs from repeated cycling and age.",
        importance: "Weak springs result in slow operation, failing to meet required clearing times.",
        approach: "Measure spring force or operating times. Replace springs when out of specification.",
        severity: "Critical",
        relatedTests: ["Timing Test", "Motion Analysis"],
        relatedMonitoring: ["Vibration Sensors"]
      },
      {
        name: "Linkage Wear",
        description: "Wear in mechanical linkages connecting mechanism to contacts.",
        importance: "Worn linkages cause timing variations and incomplete operations.",
        approach: "Inspect linkage pins, bearings, and connections. Replace worn components.",
        severity: "Moderate",
        relatedTests: ["Motion Analysis", "Timing Test"],
        relatedMonitoring: ["Vibration Sensors"]
      },
      {
        name: "Motor Failure",
        description: "Failure of charging motor preventing spring charging for operations.",
        importance: "Without charged springs, breaker cannot operate on demand. Safety critical.",
        approach: "Check motor windings, bearings, and supply. Repair or replace motor.",
        severity: "Critical",
        relatedTests: ["Motor Current Test", "Timing Test"],
        relatedMonitoring: ["Coil Current Signature"]
      },
      {
        name: "Timing Drift",
        description: "Gradual change in operating times from baseline due to wear or adjustment.",
        importance: "Out-of-spec timing can cause protection coordination failures.",
        approach: "Remeasure and adjust timing. May need mechanism overhaul if wear is cause.",
        severity: "Moderate",
        relatedTests: ["Timing Test", "Motion Analysis"],
        relatedMonitoring: ["Vibration Sensors"]
      },
      {
        name: "Lubrication Issues",
        description: "Inadequate or degraded lubrication causing increased friction and wear.",
        importance: "Poor lubrication accelerates wear and can cause mechanism binding.",
        approach: "Clean and relubricate per manufacturer specifications. Use correct lubricants.",
        severity: "Moderate",
        relatedTests: ["Motion Analysis"],
        relatedMonitoring: ["Vibration Sensors"]
      },
      {
        name: "Auxiliary Contact Wear",
        description: "Wear of auxiliary contacts used for control and indication circuits.",
        importance: "Failed aux contacts can cause false indications or control malfunctions.",
        approach: "Inspect and test auxiliary contacts. Replace worn contacts.",
        severity: "Low",
        relatedTests: ["Timing Test"],
        relatedMonitoring: ["Aux Switch Monitoring"]
      }
    ],
    tests: [
      {
        name: "Timing Test",
        description: "Measurement of breaker open, close, and open-close times.",
        importance: "Critical for protection coordination. Out-of-spec times indicate mechanism problems.",
        approach: "Record contact state during operations. Measure all critical time intervals.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Spring Fatigue", "Linkage Wear", "Motor Failure", "Timing Drift", "Auxiliary Contact Wear"]
      },
      {
        name: "Motion Analysis",
        description: "High-speed recording and analysis of mechanism motion during operations.",
        importance: "Reveals subtle problems not visible in timing tests alone.",
        approach: "Use displacement sensors or high-speed video. Analyze motion profiles.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Spring Fatigue", "Linkage Wear", "Timing Drift", "Lubrication Issues"]
      },
      {
        name: "Trip Coil Test",
        description: "Testing of trip coil operation including pickup voltage and current.",
        importance: "Verifies breaker will trip when commanded. Essential safety test.",
        approach: "Measure coil resistance. Test pickup and hold-in at various voltages.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: []
      },
      {
        name: "Motor Current Test",
        description: "Measurement and analysis of charging motor current signature.",
        importance: "Reveals motor condition, spring charging issues, and mechanism drag.",
        approach: "Record motor current during charge cycle. Analyze waveform shape and timing.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Motor Failure"]
      }
    ],
    monitoring: [
      {
        name: "Aux Switch Monitoring",
        description: "Monitoring of auxiliary contact position for status and operations counting.",
        importance: "Tracks operations for maintenance planning. Detects position discrepancies.",
        approach: "Wire aux contacts to monitoring system. Count operations and verify positions.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Auxiliary Contact Wear"]
      },
      {
        name: "Coil Current Signature",
        description: "Recording and analysis of trip/close coil currents during operations.",
        importance: "Non-invasive test reveals coil and mechanism condition.",
        approach: "Install current sensors on coil circuits. Analyze signatures for anomalies.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Motor Failure"]
      },
      {
        name: "Vibration Sensors",
        description: "Sensors monitoring mechanical vibration during breaker operations.",
        importance: "Vibration patterns reveal mechanism health and developing problems.",
        approach: "Mount accelerometers on mechanism. Record and analyze during operations.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Spring Fatigue", "Linkage Wear", "Timing Drift", "Lubrication Issues"]
      }
    ]
  },
  {
    id: "4",
    number: 4,
    name: "Arc Extinction Chamber",
    color: "#EF4444",
    failureModes: [
      {
        name: "Arc Chute Damage",
        description: "Damage to arc chute plates or structure from repeated arcing during interruptions.",
        importance: "Damaged arc chutes may fail to extinguish arcs, causing continued arcing and damage.",
        approach: "Inspect arc chutes visually. Replace damaged plates or entire chute assembly.",
        severity: "Critical",
        relatedTests: ["Arc Chamber Inspection"],
        relatedMonitoring: []
      },
      {
        name: "Nozzle Erosion",
        description: "Wear of blast nozzles in gas circuit breakers from arc exposure.",
        importance: "Eroded nozzles reduce interrupting capability and gas flow efficiency.",
        approach: "Inspect nozzles for wear. Replace when erosion reaches limits.",
        severity: "Moderate",
        relatedTests: ["Arc Chamber Inspection", "SF6 Gas Analysis"],
        relatedMonitoring: ["SF6 Density Monitors"]
      },
      {
        name: "SF6 Leakage",
        description: "Loss of SF6 insulating and arc-quenching gas from the interrupter.",
        importance: "Low SF6 pressure reduces interrupting and insulating capability. Safety hazard.",
        approach: "Locate and repair leaks using leak detector. Top up gas to rated pressure.",
        severity: "Critical",
        relatedTests: ["SF6 Gas Analysis"],
        relatedMonitoring: ["SF6 Density Monitors", "Leak Detectors"]
      },
      {
        name: "Vacuum Loss",
        description: "Loss of vacuum integrity in vacuum interrupter allowing air ingress.",
        importance: "Vacuum interrupters require high vacuum for operation. Loss means failure to interrupt.",
        approach: "Test vacuum with hi-pot or magnetron. Replace interrupter if vacuum is lost.",
        severity: "Critical",
        relatedTests: ["Vacuum Integrity Test"],
        relatedMonitoring: []
      },
      {
        name: "Carbon Deposits",
        description: "Buildup of carbon from arcing on interrupter components.",
        importance: "Carbon contamination can cause tracking and reduce interrupting capability.",
        approach: "Clean carbon deposits during maintenance. Severe buildup may require replacement.",
        severity: "Moderate",
        relatedTests: ["Arc Chamber Inspection"],
        relatedMonitoring: []
      }
    ],
    tests: [
      {
        name: "SF6 Gas Analysis",
        description: "Laboratory analysis of SF6 gas for moisture, purity, and decomposition products.",
        importance: "Reveals gas condition and any internal arcing problems. Required for SF6 equipment.",
        approach: "Sample gas and analyze for moisture, SF6 purity, and arc byproducts.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Nozzle Erosion", "SF6 Leakage"]
      },
      {
        name: "Vacuum Integrity Test",
        description: "Testing of vacuum level in vacuum interrupters using electrical or magnetic methods.",
        importance: "Only way to verify vacuum condition without destructive testing.",
        approach: "Apply high voltage across open contacts. Measure current or use magnetron gauge.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Vacuum Loss"]
      },
      {
        name: "Arc Chamber Inspection",
        description: "Visual inspection of arc interruption chamber components.",
        importance: "Direct observation of wear and damage. Required during maintenance.",
        approach: "Open breaker and inspect all arc path components. Document and photograph condition.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Arc Chute Damage", "Nozzle Erosion", "Carbon Deposits"]
      }
    ],
    monitoring: [
      {
        name: "SF6 Density Monitors",
        description: "Continuous monitoring of SF6 gas density (pressure compensated for temperature).",
        importance: "Immediate warning of gas leakage. Required for safe SF6 equipment operation.",
        approach: "Install density gauge with alarm contacts. Connect to SCADA and protection.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Nozzle Erosion", "SF6 Leakage"]
      },
      {
        name: "Leak Detectors",
        description: "Sensitive detectors for identifying SF6 leak locations.",
        importance: "Essential for finding and repairing leaks. SF6 is potent greenhouse gas.",
        approach: "Use handheld sniffer or infrared camera to locate leaks during inspections.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["SF6 Leakage"]
      }
    ]
  },
  {
    id: "5",
    number: 5,
    name: "Control & Protection",
    color: "#8B5CF6",
    failureModes: [
      {
        name: "Relay Malfunction",
        description: "Protective relay failing to operate correctly due to hardware or software issues.",
        importance: "Failed protection can allow faults to persist, causing equipment damage and safety hazards.",
        approach: "Test relay operation periodically. Update firmware. Replace faulty relays.",
        severity: "Critical",
        relatedTests: ["Relay Testing"],
        relatedMonitoring: ["IED Monitoring"]
      },
      {
        name: "CT/PT Errors",
        description: "Current or potential transformers providing inaccurate measurements to protection.",
        importance: "Metering and protection accuracy depends on CT/PT accuracy. Errors cause misoperation.",
        approach: "Test ratio, polarity, and accuracy. Recalibrate or replace as needed.",
        severity: "Critical",
        relatedTests: ["CT/PT Ratio Test"],
        relatedMonitoring: ["IED Monitoring"]
      },
      {
        name: "Wiring Degradation",
        description: "Deterioration of control and protection wiring from age, heat, or physical damage.",
        importance: "Wiring failures can cause misoperation or failure to trip.",
        approach: "Inspect wiring during maintenance. Megger test circuits. Replace degraded wiring.",
        severity: "Moderate",
        relatedTests: ["Wiring Continuity"],
        relatedMonitoring: []
      },
      {
        name: "Communication Failure",
        description: "Loss of communication between protective devices, SCADA, or between substations.",
        importance: "Modern protection schemes rely on communication. Failure can disable protection.",
        approach: "Check communication equipment and links. Repair or replace failed components.",
        severity: "Moderate",
        relatedTests: [],
        relatedMonitoring: ["Network Diagnostics"]
      },
      {
        name: "Battery Issues",
        description: "Problems with station battery including low capacity, ground faults, or cell failures.",
        importance: "Battery supplies power for tripping and control. Failure prevents breaker operation.",
        approach: "Test battery capacity and cell condition. Replace weak cells or entire battery.",
        severity: "Critical",
        relatedTests: [],
        relatedMonitoring: ["Battery Monitors"]
      }
    ],
    tests: [
      {
        name: "Relay Testing",
        description: "Comprehensive testing of protective relay settings, logic, and trip functionality.",
        importance: "Verifies protection will operate correctly for all fault types. Essential safety test.",
        approach: "Inject test currents and voltages. Verify operation times and logic. Test all functions.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Relay Malfunction"]
      },
      {
        name: "CT/PT Ratio Test",
        description: "Verification of current and potential transformer ratios and polarities.",
        importance: "Incorrect ratios cause metering errors and protection misoperation.",
        approach: "Apply known current/voltage. Measure secondary output. Verify polarity markings.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["CT/PT Errors"]
      },
      {
        name: "Wiring Continuity",
        description: "Testing of control and protection wiring for continuity and insulation.",
        importance: "Verifies wiring integrity. Finds open circuits and insulation failures.",
        approach: "Continuity test all circuits. Megger test insulation. Document results.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Wiring Degradation"]
      }
    ],
    monitoring: [
      {
        name: "IED Monitoring",
        description: "Continuous monitoring of intelligent electronic device (relay) health and status.",
        importance: "Provides real-time status of protection system. Alerts to any abnormalities.",
        approach: "Enable relay self-monitoring features. Connect to SCADA for alarming.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Relay Malfunction", "CT/PT Errors"]
      },
      {
        name: "Battery Monitors",
        description: "Continuous monitoring of station battery voltage, current, and cell conditions.",
        importance: "Battery is critical for protection operation. Monitoring ensures readiness.",
        approach: "Install battery monitor with cell-level monitoring. Set alarms for abnormal conditions.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Battery Issues"]
      },
      {
        name: "Network Diagnostics",
        description: "Monitoring of protection system communication networks.",
        importance: "Communication is critical for modern protection. Monitoring detects problems early.",
        approach: "Monitor link status, error rates, and latency. Alert on communication failures.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Communication Failure"]
      }
    ]
  }
];

export const getSimpleSwitchgearComponents = () => {
  return SWITCHGEAR_DATA.map(comp => ({
    id: comp.id,
    number: comp.number,
    name: comp.name,
    color: comp.color,
    failureModes: comp.failureModes.map(f => f.name),
    tests: comp.tests.map(t => t.name),
    monitoring: comp.monitoring.map(m => m.name)
  }));
};
